import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AccessDenied from '../AccessDenied/AccessDenied';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireDocumentSession?: boolean;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireDocumentSession = false,
  requireAdmin = false
}) => {
  const { user, hasGroupAccess, checkGroupAccess } = useAuth();
  const location = useLocation();
  const [isCheckingGroupAccess, setIsCheckingGroupAccess] = useState(false);

  useEffect(() => {
    console.log('🔍 === PROTECTED ROUTE ANALYSIS ===');
    console.log('🔍 Current pathname:', location.pathname);
    console.log('🔍 Current search:', location.search);
    console.log('🔍 User logged in?', !!user);
    console.log('🔍 User object:', user);
    
    // Always save URL if there are important parameters, regardless of login status
    const hasXml = location.search.includes('xml=');
    const hasDocId = location.search.includes('docId=');
    const hasGet = location.search.includes('get&');
    const hasPVersion = location.search.includes('pVersion=');
    const hasContRep = location.search.includes('contRep=');
    const hasAccessMode = location.search.includes('accessMode=');
    const hasAuthId = location.search.includes('authId=');
    
    const hasImportantParams = hasXml || hasDocId || hasGet || hasPVersion;
    const isDocumentViewerRoute = location.pathname.includes('/document-viewer');
    
    console.log('🔍 Parameter analysis:');
    console.log('   📄 Has XML param?', hasXml);
    console.log('   🆔 Has docId param?', hasDocId);
    console.log('   🔗 Has get param?', hasGet);
    console.log('   📋 Has pVersion param?', hasPVersion);
    console.log('   🏢 Has contRep param?', hasContRep);
    console.log('   🔐 Has accessMode param?', hasAccessMode);
    console.log('   👤 Has authId param?', hasAuthId);
    console.log('   ⭐ Has important params?', hasImportantParams);
    console.log('   📁 Is document-viewer route?', isDocumentViewerRoute);
    
    // Save URL if: not logged in, OR document-viewer route with important params, OR any route with important params
    const shouldSaveUrl = !user || (isDocumentViewerRoute && hasImportantParams) || hasImportantParams;
    console.log('🔍 Should save URL?', shouldSaveUrl);
    
    if (shouldSaveUrl) {
      const fullUrl = location.pathname + location.search;
      
      console.log('💾 === SAVING URL BEFORE LOGIN ===');
      console.log('💾 Full URL to save:', fullUrl);
      console.log('💾 URL length:', fullUrl.length);
      console.log('💾 URL preview (first 200 chars):', fullUrl.substring(0, 200) + '...');
      
      // Clear any existing redirect URL first
      sessionStorage.removeItem('redirectAfterLogin');
      localStorage.removeItem('redirectAfterLogin');
      
      sessionStorage.setItem('redirectAfterLogin', fullUrl);
      localStorage.setItem('redirectAfterLogin', fullUrl);
      
      console.log('💾 Saved to sessionStorage:', sessionStorage.getItem('redirectAfterLogin'));
      console.log('💾 Saved to localStorage:', localStorage.getItem('redirectAfterLogin'));
      console.log('💾 SessionStorage length:', sessionStorage.length);
      console.log('💾 LocalStorage length:', localStorage.length);
      
      // Show all sessionStorage keys
      console.log('💾 All sessionStorage keys:');
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          console.log(`   ${key}: ${sessionStorage.getItem(key)?.substring(0, 100)}...`);
        }
      }
      
      console.log('💾 === URL SAVED SUCCESSFULLY ===');
    } else {
      console.log('❌ Not saving URL - no important parameters or conditions not met');
    }
    
    console.log('🔍 === END PROTECTED ROUTE ANALYSIS ===');
  }, [location, user]);

  // Check group access if user is logged in but we haven't checked yet
  useEffect(() => {
    const verifyGroupAccess = async () => {
      if (user && !hasGroupAccess) {
        setIsCheckingGroupAccess(true);
        const hasAccess = await checkGroupAccess();
        setIsCheckingGroupAccess(false);
        
        if (!hasAccess) {
          // The alert and logout will be handled in the AuthContext
          return;
        }
      }
    };

    verifyGroupAccess();
  }, [user, hasGroupAccess, checkGroupAccess]);

  // Check if main session is still valid
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while checking group access
  if (isCheckingGroupAccess) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1">
          Verificando acceso a grupos...
        </Typography>
      </Box>
    );
  }

  // If user doesn't have group access, show access denied
  if (user && !hasGroupAccess && user.username !== 'admin') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 2,
        p: 3
      }}>
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Acceso Denegado
          </Typography>
          <Typography variant="body1">
            Usuario satisfactoriamente logueado pero no tenemos un grupo asignado para ti. 
            Por favor contacta a tu administrador para que te asigne a un grupo y te otorgue permisos de acceso.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // If document session is required but not valid, redirect to login
  if (requireDocumentSession && !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if admin access is required
  if (requireAdmin && user.username !== 'admin') {
    // Show access denied page for non-admin users
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 